/**
 * usePushSync — FCM push token registration and notification handling.
 *
 * Runs inside AppLayout (within DatabaseProvider).
 *
 * Responsibilities:
 * 1. Check notification permission (passive, no prompt)
 * 2. Fetch FCM token from FirebaseMessaging
 * 3. Store token in Dexie Cloud push_token table
 * 4. Listen for token refresh and update
 * 5. Listen for incoming push notifications and trigger Dexie sync
 * 6. Handle force_logout push (verify + logout in foreground)
 *
 * Native only — no-op on web.
 *
 * Ported from v1: totodo-isolated-example/src/hooks/usePushSync.ts
 */

import { useEffect } from "react"
import { Capacitor } from "@capacitor/core"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

import { db } from "@/db/schema"
import { getStableDeviceId } from "@/shared/utils/device/stableDeviceId"
import { checkDeviceStatus, updateDeviceStatusWithRetry } from "@/features/auth/services/deviceSync"
import { useAuthStore } from "@/features/auth/stores/authStore"
import { logPush } from "@/shared/telemetry/client"
import { addBreadcrumb } from "@/shared/telemetry/breadcrumbs"
import { logError } from "@/shared/telemetry/errorStorage"

dayjs.extend(utc)

// ---------------------------------------------------------------------------
// Types — lazy-loaded to avoid bundling on web
// ---------------------------------------------------------------------------

type FirebaseMessagingModule = typeof import("@capacitor-firebase/messaging")

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getCurrentUserId(): string | null {
  try {
    const userId = db.cloud.currentUserId
    return userId && userId !== "unauthorized" ? userId : null
  } catch {
    return null
  }
}

// ---------------------------------------------------------------------------
// usePushSync
// ---------------------------------------------------------------------------

export function usePushSync(): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    if (!Capacitor.isPluginAvailable("FirebaseMessaging")) return

    let tokenRefreshHandle: { remove: () => Promise<void> | void } | null =
      null
    let notificationHandle: { remove: () => Promise<void> | void } | null =
      null
    let appStateHandle: { remove: () => Promise<void> | void } | null = null
    let mounted = true

    const run = async () => {
      // Dynamic imports to keep web bundle small
      const { FirebaseMessaging } = (await import(
        "@capacitor-firebase/messaging"
      )) as FirebaseMessagingModule
      const { App } = await import("@capacitor/app")

      // ── Helpers ──

      const savePushToken = async (tokenOverride?: string) => {
        try {
          if (useAuthStore.getState().initialSyncActive) return

          const { token } = tokenOverride
            ? { token: tokenOverride }
            : await FirebaseMessaging.getToken()

          if (!token) return

          const userId = getCurrentUserId()
          if (!userId) return

          const deviceId = getStableDeviceId(userId)
          const platform = Capacitor.getPlatform()

          // Skip if token is already active for this device
          const existing = await db.push_token
            .where({ deviceId, platform, token, status: "active" })
            .first()
          if (existing) return

          const now = dayjs.utc().valueOf().toString()

          await db.transaction("rw", db.push_token, async () => {
            // Revoke old tokens for same device+platform
            const oldTokens = await db.push_token
              .where({ deviceId, platform, status: "active" })
              .toArray()
            for (const old of oldTokens) {
              if (old.id) {
                await db.push_token.update(old.id, {
                  status: "revoked",
                  updatedAt: now,
                })
              }
            }
            // Store new token
            await db.push_token.put({
              id: crypto.randomUUID(),
              deviceId,
              platform,
              createdAt: now,
              updatedAt: now,
              token,
              provider: "fcm",
              status: "active",
            })
          })

          logPush("register", { platform })
          addBreadcrumb({ category: "lifecycle", message: "[PushSync] token saved", data: { platform } })
        } catch (err) {
          console.warn("[PushSync] Failed to save FCM token:", err)
          logPush("error", { phase: "save_token" })
          logError(err instanceof Error ? err : new Error(String(err)), {
            source: "manual",
            severity: "warning",
            category: "lifecycle",
            data: { hook: "usePushSync", phase: "save_token" },
          })
        }
      }

      const isPermissionGranted = async (): Promise<boolean> => {
        try {
          if (useAuthStore.getState().initialSyncActive) return false
          const result = await FirebaseMessaging.checkPermissions()
          return (result as { receive?: string }).receive === "granted"
        } catch {
          return false
        }
      }

      // ── 1. Bootstrap: check permission + save initial token ──
      const granted = await isPermissionGranted()
      if (granted && mounted) {
        // On iOS, defer until APNS registration completes (tokenReceived fires)
        if (Capacitor.getPlatform() !== "ios") {
          await savePushToken()
        }
      }

      // ── 2. Token refresh listener ──
      if (mounted) {
        tokenRefreshHandle = await FirebaseMessaging.addListener(
          "tokenReceived",
          async (event) => {
            if (event?.token) {
              logPush("token", { source: "refresh" })
              addBreadcrumb({ category: "lifecycle", message: "[PushSync] token refreshed" })
              await savePushToken(event.token)
            }
          },
        )
      }

      // ── 3. Notification listener → trigger Dexie sync or force-logout ──
      if (mounted) {
        notificationHandle = await FirebaseMessaging.addListener(
          "notificationReceived",
          async (event) => {
            const data = (event?.notification?.data || {}) as Record<
              string,
              unknown
            >
            if (data.type === "force_logout") {
              logPush("received", { type: "force_logout" })
              addBreadcrumb({ category: "lifecycle", message: "[PushSync] force_logout push received" })
              // Verify via API before acting (guard against spoofed push)
              try {
                const userId = getCurrentUserId()
                if (!userId) return
                const deviceId = getStableDeviceId(userId)
                const status = await checkDeviceStatus(deviceId)
                if (status === "force_logout") {
                  console.warn("[PushSync] force_logout confirmed — wiping local data")
                  await updateDeviceStatusWithRetry(deviceId, "logged_out", 3)
                  window.location.href = "/logout"
                }
              } catch (e) {
                console.warn("[PushSync] force_logout verification failed:", e)
              }
            } else if (data.type === "sync") {
              logPush("received", { type: "sync" })
              addBreadcrumb({ category: "lifecycle", message: "[PushSync] sync push received" })
              if (useAuthStore.getState().initialSyncActive) return
              try {
                await db.cloud.sync()
              } catch (e) {
                console.warn("[PushSync] Sync after push failed:", e)
                logPush("error", { phase: "sync_after_push" })
              }
            }
          },
        )
      }

      // ── 4. Re-check permission on app foreground (may have been granted in Settings) ──
      if (mounted && Capacitor.isPluginAvailable("App")) {
        try {
          appStateHandle = await App.addListener(
            "appStateChange",
            ({ isActive }) => {
              if (isActive && !useAuthStore.getState().initialSyncActive) {
                void isPermissionGranted().then((granted) => {
                  if (granted) void savePushToken()
                })
              }
            },
          )
        } catch {
          // App plugin not available — skip
        }
      }
    }

    void run()

    return () => {
      mounted = false
      tokenRefreshHandle?.remove()
      notificationHandle?.remove()
      appStateHandle?.remove()
    }
  }, [])
}
